import DOMPurify from 'dompurify'
import {
  ARTICLE_TAGS,
  BLOG_TAGS,
  GLOBAL_ATTRIBUTES,
  IFRAME_ATTRIBUTES,
  LABEL_TAGS,
  LINK_ATTRIBUTES,
  MEDIA_ATTRIBUTES,
  IMAGE_ATTRIBUTES,
  SAFE_CSS_VALUE,
  SAFE_STYLE_PROPERTIES,
  SOURCE_ATTRIBUTES,
  TABLE_ATTRIBUTES,
  TRACK_ATTRIBUTES,
  browserOptions,
} from './sanitizeHtml.shared.ts'

const generalAllowedAttributes = {
  '*': GLOBAL_ATTRIBUTES,
  a: LINK_ATTRIBUTES,
  audio: MEDIA_ATTRIBUTES,
  img: IMAGE_ATTRIBUTES,
  source: SOURCE_ATTRIBUTES,
  td: TABLE_ATTRIBUTES,
  th: TABLE_ATTRIBUTES,
  track: TRACK_ATTRIBUTES,
  video: MEDIA_ATTRIBUTES,
}

const labelAllowedAttributes = {
  '*': ['class', 'style', 'title', 'aria-*', 'data-*'],
  a: LINK_ATTRIBUTES,
}

const blogAllowedAttributes = {
  ...generalAllowedAttributes,
  iframe: IFRAME_ATTRIBUTES,
}

const safeStyleProperties = new Set(SAFE_STYLE_PROPERTIES)

function splitSerializedDeclarations(cssText: string): string[] {
  const declarations: string[] = []
  let start = 0
  let quote = ''
  let escaped = false
  let parentheses = 0

  for (let index = 0; index < cssText.length; index += 1) {
    const character = cssText[index]

    if (quote) {
      if (escaped) {
        escaped = false
      }
      else if (character === '\\') {
        escaped = true
      }
      else if (character === quote) {
        quote = ''
      }
      continue
    }

    if (character === '"' || character === "'") {
      quote = character
    }
    else if (character === '(') {
      parentheses += 1
    }
    else if (character === ')' && parentheses > 0) {
      parentheses -= 1
    }
    else if (character === ';' && parentheses === 0) {
      declarations.push(cssText.slice(start, index))
      start = index + 1
    }
  }

  if (start < cssText.length) {
    declarations.push(cssText.slice(start))
  }

  return declarations
}

DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
  if (data.attrName !== 'style') {
    return
  }

  const parsedStyle = document.createElement('div').style
  parsedStyle.cssText = data.attrValue
  const safeStyle = document.createElement('div').style

  for (const declaration of splitSerializedDeclarations(parsedStyle.cssText)) {
    const separatorIndex = declaration.indexOf(':')
    if (separatorIndex <= 0) {
      continue
    }

    const property = declaration.slice(0, separatorIndex).trim().toLowerCase()
    const value = parsedStyle.getPropertyValue(property).trim()
    if (safeStyleProperties.has(property) && value && SAFE_CSS_VALUE.test(value)) {
      safeStyle.setProperty(property, value, parsedStyle.getPropertyPriority(property))
    }
  }

  data.attrValue = safeStyle.cssText
  if (!data.attrValue) {
    data.keepAttr = false
  }
})
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.getAttribute?.('target')?.toLowerCase() === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer')
  }
})

export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, browserOptions(ARTICLE_TAGS, generalAllowedAttributes))
}

export function sanitizeLabelHtml(html: string): string {
  return DOMPurify.sanitize(html, browserOptions(LABEL_TAGS, labelAllowedAttributes))
}

export function sanitizeBlogHtml(html: string): string {
  return DOMPurify.sanitize(html, browserOptions(BLOG_TAGS, blogAllowedAttributes))
}
